/**
 * `LineChart` — spec §F2 `LineChart(series[{name,short,color,values}], {unit})`:
 * "gridlines #EEF1EC, y-ticks, direct end-labels, hover dots with tooltip".
 *
 * The mockup's `lineChart()`, with the three changes real data forces — the same three
 * `Spark` already made, for the same reasons:
 *
 * 1. **Nulls break the line.** §H1's `series[].values` carries `null` for a year a unit did
 *    not report, and a polyline drawn straight across the gap is a fabricated trend. The
 *    series is split into runs of consecutive present points; an isolated point is drawn as
 *    a dot with no line.
 * 2. **The end label goes on the last point that exists**, not on `values[values.length-1]`
 *    — which for a series ending in a gap is `null`, and `Y(null)` is `NaN`, i.e. a label
 *    that silently disappears from the SVG.
 * 3. **Hover through the shared `ChartTip` portal** (§F2), not the mockup's `data-tip` pass.
 *
 * Every number on screen is an ink token, never the series colour (§F2/§G8); the colour is
 * carried by the line, the dot ring and the direct label, which is a *name* and not a value.
 *
 * The right margin is the direct labels' gutter: §G8 requires them for ≥2 series, and a
 * label drawn past the viewBox is the same as no label at all.
 */
import { useMemo } from 'react';

import { fmtNum } from '../../lib/format';
import { hideChartTip, showChartTip } from './chartTipStore';

/** The mockup's `padL/padR/padT/padB`, plus the label gutter its viewBox added (`w + 34`). */
export const LINE_PAD_LEFT = 44;
export const LINE_PAD_RIGHT = 14;
export const LINE_PAD_TOP = 14;
export const LINE_PAD_BOTTOM = 26;
export const LINE_LABEL_GUTTER = 34;
/** Mockup: five horizontal rules (`ticks = 4`, drawn inclusive of both ends). */
export const LINE_TICKS = 4;
/** §F2's gridline ink. */
export const GRID_INK = '#EEF1EC';
const AXIS_INK = '#8B958A';

export interface LineSeries {
  name: string;
  /** The direct end label; falls back to `name`. */
  short?: string;
  color: string;
  /** One per `labels` entry; `null`/non-finite = no data for that period. */
  values: readonly (number | null | undefined)[];
}

export interface LineChartProps {
  series: readonly LineSeries[];
  /** X categories — normally the §F1 years. */
  labels: readonly (string | number)[];
  w?: number;
  h?: number;
  /** Appended to the tooltip value. */
  unit?: string;
  /** §G3 decimals for the tooltip; the y ticks stay whole numbers, as in the mockup. */
  fmt?: number;
  ariaLabel?: string;
  className?: string;
}

interface Point {
  i: number;
  v: number;
  x: number;
  y: number;
}

/** Consecutive present points — the only segments that may legitimately be joined. */
function runsOf(points: readonly Point[]): Point[][] {
  const runs: Point[][] = [];
  let current: Point[] = [];
  for (const p of points) {
    const last = current[current.length - 1];
    if (last && p.i !== last.i + 1) {
      runs.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length) runs.push(current);
  return runs;
}

export function LineChart({
  series,
  labels,
  w = 330,
  h = 190,
  unit = '',
  fmt = 2,
  ariaLabel,
  className,
}: LineChartProps) {
  const chart = useMemo(() => {
    const n = Math.max(labels.length, 1);
    const numbers = series.map((s) =>
      labels.map((_, i) => {
        const v = s.values[i];
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
      }),
    );
    const present = numbers.flat().filter((v): v is number => v !== null);
    let min = present.length ? Math.min(...present) : 0;
    let max = present.length ? Math.max(...present) : 1;
    // The mockup's baseline rule: a positive series that does not start near its maximum
    // reads better from zero than from its own floor.
    if (min > 0 && min < max * 0.4) min = 0;
    const span = max - min || 1;
    max += span * 0.08;
    const range = max - min || 1;

    const X = (i: number) => LINE_PAD_LEFT + (n > 1 ? (i * (w - LINE_PAD_LEFT - LINE_PAD_RIGHT)) / (n - 1) : 0);
    const Y = (v: number) =>
      LINE_PAD_TOP + (1 - (v - min) / range) * (h - LINE_PAD_TOP - LINE_PAD_BOTTOM);

    const ticks = Array.from({ length: LINE_TICKS + 1 }, (_, i) => {
      const v = min + (i * range) / LINE_TICKS;
      return { v, y: Y(v) };
    });

    const lines = series.map((s, si) => {
      const points: Point[] = [];
      (numbers[si] ?? []).forEach((v, i) => {
        if (v !== null) points.push({ i, v, x: X(i), y: Y(v) });
      });
      const last = points[points.length - 1];
      return {
        series: s,
        points,
        runs: runsOf(points),
        end: last ? { x: w - LINE_PAD_RIGHT + 2, y: last.y + 3.5 } : null,
      };
    });

    return { ticks, lines, X };
  }, [series, labels, w, h]);

  const tipValue = (v: number) => `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;

  return (
    <svg
      className={`chart linechart${className ? ` ${className}` : ''}`}
      width="100%"
      viewBox={`0 0 ${w + LINE_LABEL_GUTTER} ${h}`}
      role="img"
      aria-label={ariaLabel ?? `${series.length} series`}
      data-series={series.length}
      onMouseLeave={hideChartTip}
    >
      {chart.ticks.map((tick, i) => (
        <g key={`tick-${i}`}>
          <line
            x1={LINE_PAD_LEFT}
            x2={w - LINE_PAD_RIGHT}
            y1={tick.y}
            y2={tick.y}
            stroke={GRID_INK}
            strokeWidth={1}
            data-gridline={i}
          />
          <text
            x={LINE_PAD_LEFT - 7}
            y={tick.y + 3.5}
            textAnchor="end"
            fontSize={9.5}
            fill={AXIS_INK}
            data-ytick={i}
          >
            {fmtNum(tick.v, 0)}
          </text>
        </g>
      ))}

      {labels.map((label, i) => (
        <text
          key={`x-${label}-${i}`}
          x={chart.X(i)}
          y={h - 8}
          textAnchor="middle"
          fontSize={9.5}
          fill={AXIS_INK}
          data-xtick={i}
        >
          {label}
        </text>
      ))}

      {chart.lines.map((line, si) => (
        <g key={`${line.series.name}-${si}`} data-line={si}>
          {line.runs.map((run, ri) =>
            run.length > 1 ? (
              <polyline
                key={`run-${ri}`}
                points={run.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={line.series.color}
                strokeWidth={2}
                strokeLinejoin="round"
              />
            ) : null,
          )}
          {line.points.map((p) => (
            <g key={`p-${p.i}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r={7}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                data-dot={`${si}-${p.i}`}
                data-value={tipValue(p.v)}
                onMouseEnter={(e) =>
                  showChartTip(e, `${line.series.name} · ${labels[p.i]}`, tipValue(p.v))
                }
                onMouseMove={(e) =>
                  showChartTip(e, `${line.series.name} · ${labels[p.i]}`, tipValue(p.v))
                }
                onMouseLeave={hideChartTip}
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={2.6}
                fill="#fff"
                stroke={line.series.color}
                strokeWidth={1.8}
                pointerEvents="none"
              />
            </g>
          ))}
          {line.end ? (
            <text
              x={line.end.x}
              y={line.end.y}
              fontSize={9.8}
              fontWeight={700}
              fill={line.series.color}
              data-endlabel={si}
            >
              {line.series.short ?? line.series.name}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}

export default LineChart;
